import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import userResources from "api/userResources";
import DateFilter from "components/DateFilter/DateFilter";
import React, { useEffect, useMemo, useState } from "react";
import { FaFileExcel } from "react-icons/fa";
import { IoMdPrint } from "react-icons/io";
import { toast } from "react-toastify";
import { logo3 } from "utils/images";
import { logo2 } from "utils/images";
import { logo1 } from "utils/images";
export default function SuppliersPayment() {
  const [isLoading, setIsLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [supplierData, setSupplierData] = useState([]);
  const [supplierId, setSupplierId] = useState();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
    totalRows: 0,
    lastPage: 0,
  });

  // 1) Before returning JSX, compute totals:
  const { totalDr, totalCr, totalBalance } = useMemo(() => {
    const result = invoices.reduce(
      (acc, inv) => {
        const supplier = inv?.airticket?.airticket_to_supplier || {};
        const paymentMade = Number(supplier.paymentMade) || 0;
        const total = Number(supplier.total) || 0;
        const received = Number(supplier.payment_received_from_supplier) || 0;

        const drRaw = paymentMade - total - received;
        const crRaw = total - paymentMade;

        const dr = drRaw < 0 ? Math.abs(drRaw) : 0;
        const cr = crRaw < 0 ? Math.abs(crRaw) : 0;
        const balance = dr - cr;

        acc.totalDr += dr;
        acc.totalCr += cr;
        acc.totalBalance += drRaw;

        return acc;
      },
      { totalDr: 0, totalCr: 0, totalBalance: 0 }
    );

    // Ensure no NaN values
    return {
      totalDr: isNaN(result.totalDr) ? 0 : result.totalDr,
      totalCr: isNaN(result.totalCr) ? 0 : result.totalCr,
      totalBalance: isNaN(result.totalBalance) ? 0 : result.totalBalance,
    };
  }, [invoices]);

  const getSuppliers = async () => {
    try {
      const response = await userResources.supplier();
      const combinedData = response.data.data.map((item) => ({
        value: item.id,
        label: item.name,
      }));

      setSupplierData(combinedData);
    } catch (error) {
      console.log(error);
    }
  };

  // Fetch invoices whenever pageIndex or pageSize changes
  const getInvoiceList = async () => {
    setIsLoading(true);
    try {
      const params = {
        limit: pagination.pageSize,
        page: pagination.pageIndex + 1,
        supplier_id: supplierId,
      };

      if (startDate) {
        params.start_date = startDate;
      }
      if (endDate) {
        params.end_date = endDate;
      }
      const response = await userResources.totalInvoice(params);
      const data = response?.data;

      setInvoices(data?.data || []);
      if (data?.paginate_data) {
        const { current_page, per_page, total, last_page } = data.paginate_data;
        setPagination((prev) => ({
          ...prev,
          pageIndex: current_page - 1,
          pageSize: per_page,
          totalRows: total,
          lastPage: last_page,
        }));
      }
    } catch (error) {
      console.error("Error fetching invoice list:", error);
      setInvoices([]);
      toast.error("Error fetching invoice list");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (newPageIndex) => {
    if (newPageIndex < 0 || newPageIndex >= pagination.lastPage) return;
    setPagination((prev) => ({ ...prev, pageIndex: newPageIndex }));
  };

  const supplierName =
    supplierData.find((s) => s.value == supplierId)?.label || "All Suppliers";

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    const printContent = document.querySelector(".printable").innerHTML;

    const printStyles = `
    <style>
      body { 
        font-family: Arial, sans-serif; 
        margin: 0.5cm; 
        color: #000; 
        background: #fff; 
      }
      .print-header { 
        display: flex;
        justify-content: space-between;
        margin-bottom: 20px; 
        padding: 20px;
        background-color: #000 !important;
        color: #fff !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .print-title { 
        font-size: 18px; 
        font-weight: bold; 
        margin-bottom: 5px;
      }
      .print-subtitle { 
        font-size: 14px; 
        margin-bottom: 10px;
      }
      .print-meta { 
        font-size: 12px; 
        margin-bottom: 5px;
      }
      table { 
        width: 100%; 
        border-collapse: collapse; 
        margin-top: 10px;
        font-size: 12px;
      }
      th, td { 
        border: 1px solid #000; 
        padding: 5px; 
        text-align: left;
        max-width: 100px;
        word-wrap: break-word;
        overflow-wrap: break-word;
        white-space: normal;
      }
      th { 
        background-color: #f2f2f2 !important; 
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        font-weight: bold;
      }
      tfoot td { 
        font-weight: bold; 
        background-color: #f2f2f2 !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .no-print { 
        display: none; 
      }
      @page { 
        size: auto; 
        margin: 0.5cm; 
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .page-break { 
          page-break-after: always; 
        }
        img {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }

      .page-number {
        display: none;
      }
      @media print {
        .page-number {
          display: block;
          position: fixed;
          bottom: 0;
          right: 0;
          font-size: 10px;
          padding: 5px 10px;
        }
        @page {
          @bottom-right {
            content: "Page " counter(page);
            font-size: 10px;
          }
        }
      }
    </style>
  `;

    const currentDate = new Date().toLocaleDateString();

    const printHeader = `
    <div class="print-header">
      <div style="flex: 1;">
        <div style="font-size: 22px; font-weight: bold;">Suppliers Payment</div>
        <div style="font-size: 16px; margin-top: 4px;">${supplierName}</div>
        <div style="font-size: 14px; margin-top: 8px;">Generated on: ${currentDate}</div>
      </div>

      <div style="flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;">
        <img src="${logo1}" alt="Logo 1" style="height: 45px; margin-bottom: 6px;" />
        <img src="${logo2}" alt="Logo 2" style="height: 13px; margin-bottom: 4px;" />
        <img src="${logo3}" alt="Logo 3" style="height: 13px;" />
      </div>

      <div style="flex: 1; text-align: right;">
        <div style="font-size: 16px;">DR: <strong>${totalDr.toFixed(
          2
        )}</strong></div>
        <div style="font-size: 16px;">CR: <strong>${totalCr.toFixed(
          2
        )}</strong></div>
        <div style="font-size: 16px;">Balance: <strong>${totalBalance.toFixed(
          2
        )}</strong></div>
      </div>
    </div>
  `;

    printWindow.document.write(`
    <html>
      <head>
        <title>Suppliers Payment Report</title>
        ${printStyles}
      </head>
      <body>
        ${printHeader}
        ${printContent}
        <div class="page-number"></div>
        
        <script>
          window.onload = function() {
            // Fallback for browsers that don't support CSS page counters
            if (typeof CSS.supports === 'undefined' || !CSS.supports('content', 'counter(page)')) {
              const totalPages = Math.ceil(document.body.scrollHeight / window.innerHeight);
              document.querySelector('.page-number').textContent = 'Page ' + (window.pageCounter || 1) + ' of ' + totalPages;
            }
            
            setTimeout(function() {
              window.print();
              window.close();
            }, 200);
          }
        </script>
      </body>
    </html>
  `);
    printWindow.document.close();
  };

  const disablePrev = pagination.pageIndex === 0;
  const disableNext = pagination.pageIndex + 1 >= pagination.lastPage;

  const handleExportExcel = () => {
    const excelData = invoices.map((inv) => {
      const createdAt = inv.created_at
        ? new Date(inv.created_at).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : "-";

      const supplier = inv?.airticket?.airticket_to_supplier || {};
      const pax = inv?.airticket?.airticket_from_pax || {};

      const paymentMade = supplier.paymentMade || 0;
      const total = supplier.total || 0;
      const received = supplier.payment_received_from_supplier || 0;

      const drRaw = paymentMade - total - received;
      const crRaw = total - paymentMade;

      const DR = drRaw < 0 ? Math.abs(drRaw).toFixed(2) : "0.00";
      const CR = crRaw < 0 ? Math.abs(crRaw).toFixed(2) : "0.00";
      const balance = isNaN(drRaw) ? "0.00" : drRaw.toFixed(2);

      return {
        "Invoice Date": createdAt,
        "Invoice No": inv.invoice_number || "-",
        "Ticket Number": inv.ticket_number || "-",
        PNR: inv.gds_locator || "-",
        "Passenger Name": inv?.customer_details?.full_name || "-",
        DR,
        CR,
        Balance: balance,
      };
    });

    // 🧮 Compute totals
    const totalDR = excelData.reduce(
      (sum, item) => sum + parseFloat(item.DR),
      0
    );
    const totalCR = excelData.reduce(
      (sum, item) => sum + parseFloat(item.CR),
      0
    );
    const totalBalance = excelData.reduce(
      (sum, item) => sum + parseFloat(item.Balance),
      0
    );

    // ➕ Add totals row
    excelData.push({
      "Invoice Date": "",
      "Invoice No": "",
      "Ticket Number": "",
      PNR: "",
      "Passenger Name": "Total",
      DR: totalDR.toFixed(2),
      CR: totalCR.toFixed(2),
      Balance: totalBalance.toFixed(2),
    });

    // 💠 Title and optional info row
    const title = "Supplier Payment Report";
    const infoRow = [];
    if (startDate) {
      infoRow.push(
        `Start Date: ${new Date(startDate).toLocaleDateString("en-GB")}`
      );
    }
    if (endDate) {
      infoRow.push(
        `End Date: ${new Date(endDate).toLocaleDateString("en-GB")}`
      );
    }
    if (supplierName && supplierName !== "All Suppliers") {
      infoRow.push(`Supplier: ${supplierName}`);
    }

    const header = [[title], infoRow];
    const blankRow = [[]];

    const columns = Object.keys(excelData[0]);
    const jsonSheet = XLSX.utils.json_to_sheet(excelData, {
      origin: header.length + 1,
    });

    const worksheet = XLSX.utils.aoa_to_sheet([...header, ...blankRow]);
    Object.entries(jsonSheet).forEach(([key, value]) => {
      worksheet[key] = value;
    });

    worksheet["!merges"] = [
      {
        s: { r: 0, c: 0 },
        e: { r: 0, c: columns.length - 1 },
      },
    ];

    const titleCell = worksheet["A1"];
    if (titleCell) {
      titleCell.s = {
        font: { sz: 18, bold: true, color: { rgb: "FF1F497D" } },
        alignment: { horizontal: "center", vertical: "center" },
        fill: { fgColor: { rgb: "FFEAF1FB" } },
      };
    }

    const headerRowIndex = header.length + 1;
    for (let c = 0; c < columns.length; c++) {
      const addr = XLSX.utils.encode_cell({ r: headerRowIndex, c });
      const cell = worksheet[addr];
      if (!cell) continue;
      cell.s = {
        font: { bold: true, color: { rgb: "FFFFFFFF" } },
        fill: { fgColor: { rgb: "FF2F75B5" } },
        alignment: { horizontal: "center" },
        border: { bottom: { style: "thin", color: { rgb: "FF000000" } } },
      };
    }

    const dataStart = headerRowIndex + 1;
    const dataEnd = dataStart + excelData.length - 1;
    for (let r = dataStart; r <= dataEnd; r++) {
      const isEven = (r - dataStart) % 2 === 0;
      for (let c = 0; c < columns.length; c++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        const cell = worksheet[addr];
        if (!cell) continue;
        cell.s = {
          fill: { fgColor: { rgb: isEven ? "FFFFFFFF" : "FFF9F9F9" } },
        };
      }
    }

    const numericCols = ["DR", "CR", "Balance"];
    numericCols.forEach((colKey) => {
      const colIdx = columns.indexOf(colKey);
      for (let r = dataStart; r <= dataEnd; r++) {
        const addr = XLSX.utils.encode_cell({ r, c: colIdx });
        const cell = worksheet[addr];
        if (!cell) continue;
        cell.t = "n";
        cell.z = "#,##0.00";
      }
    });

    worksheet["!cols"] = columns.map(() => ({ wch: 20 }));
    worksheet["!freeze"] = { xSplit: 0, ySplit: headerRowIndex + 1 };
    worksheet["!autofilter"] = {
      ref: XLSX.utils.encode_range({
        s: { r: headerRowIndex, c: 0 },
        e: { r: dataEnd, c: columns.length - 1 },
      }),
    };

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "PassengerPayments");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    const safeSupplierName = supplierName
      ? supplierName.replace(/[^a-z0-9]/gi, "_")
      : "All_Suppliers";

    const formatDate = (date) => {
      if (!date) return "NA";
      return new Date(date).toLocaleDateString("en-GB").replace(/\//g, "-");
    };

    const formattedStartDate = formatDate(startDate);
    const formattedEndDate = formatDate(endDate);

    saveAs(
      blob,
      `Supplier_Payment_Report_${new Date()
        .toISOString()
        .slice(
          0,
          10
        )}_${safeSupplierName}_Start_Date_${formattedStartDate}_End_Date_${formattedEndDate}.xlsx`
    );
  };

  useEffect(() => {
    getSuppliers();
  }, []);

  useEffect(() => {
    getInvoiceList();
  }, [pagination.pageIndex, pagination.pageSize]);

  useEffect(() => {
    if (pagination.pageIndex != 0) {
      setPagination({
        pageIndex: 0,
        pageSize: 10,
        totalRows: 0,
        lastPage: 0,
      });
    } else {
      getInvoiceList();
    }
  }, [supplierId]);

  useEffect(() => {
    if (pagination.pageIndex != 0) {
      setPagination({
        pageIndex: 0,
        pageSize: 10,
        totalRows: 0,
        lastPage: 0,
      });
    } else {
      getInvoiceList();
    }
  }, [startDate, endDate]);

  return (
    <section>
      <div className="no-print grid grid-cols-1 justify-between gap-5 py-4 pt-2 text-black md:grid-cols-2">
        <div className="relative -top-1 flex items-center gap-4 rounded-full bg-gray-300 p-3">
          <div>
            <label className="md:whitespace-nowrap">Supplier:</label>
          </div>
          <div className="w-96">
            <select
              value={supplierId}
              onChange={(e) => {
                const value = e.target.value;
                setSupplierId(value);
              }}
              className="w-full bg-gray-300 text-gray-900"
            >
              <option value="">Select All...</option>
              {supplierData.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <FaFileExcel
            onClick={handleExportExcel}
            size={25}
            className="ml-4 cursor-pointer text-green-600"
          />
        </div>
        <div className="flex justify-between gap-2 rounded-full bg-gray-300 p-3">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label
              className={`flex items-end justify-between gap-5 whitespace-nowrap text-sm font-medium text-gray-900 sm:items-start `}
            >
              Start Date:{" "}
              <DateFilter
                selectedDate={startDate}
                setSelectedDate={setStartDate}
              />
            </label>
            <label
              className={`flex items-end justify-between gap-5 whitespace-nowrap text-sm font-medium text-gray-900 sm:items-start `}
            >
              End Date:{" "}
              <DateFilter
                selectedDate={endDate}
                setSelectedDate={setEndDate}
                minDate={startDate} // Pass Start Date as the minimum date for End Date
              />
            </label>
          </div>

          <IoMdPrint
            onClick={handlePrint}
            size={25}
            className="cursor-pointer text-gray-900 dark:text-white"
          />
        </div>
      </div>

      <div
        className={`${
          isLoading ? "h-[70vh]" : ""
        } printable bg-gray-900 text-white`}
      >
        <table className="min-w-full table-fixed">
          <thead>
            <tr>
              <th className="w-24 px-2 py-2 text-left text-xs font-medium uppercase">
                Invoice Date
              </th>
              <th className="w-24 px-2 py-2 text-left text-xs font-medium uppercase">
                Invoice No.
              </th>
              <th className="w-36 whitespace-nowrap px-2 py-4 text-left text-xs font-medium uppercase">
                Ticket Number
              </th>
              <th className="w-24 px-2 py-2 text-left text-xs font-medium uppercase">
                PNR
              </th>
              <th className="w-24 px-2 py-2 text-left text-xs font-medium uppercase">
                Passenger Name
              </th>
              <th className="w-24 px-2 py-2 text-left text-xs font-medium uppercase">
                DR
              </th>
              <th className="w-24 px-2 py-2 text-left text-xs font-medium uppercase">
                CR
              </th>
              <th className="w-24 px-2 py-2 text-left text-xs font-medium uppercase">
                Balance
              </th>
            </tr>
          </thead>

          {isLoading ? (
            <tbody>
              <tr>
                <td colSpan={7} className="py-6">
                  <div className="flex w-full items-center justify-center">
                    <span className="loader"></span>
                  </div>
                </td>
              </tr>
            </tbody>
          ) : (
            <>
              <tbody>
                {invoices.map((inv, idx) => (
                  <tr key={idx}>
                    <td className="px-2 py-2 text-sm">
                      {inv.created_at
                        ? new Date(inv.created_at).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short", // use "long" for full month name
                            year: "numeric",
                          })
                        : "-"}
                    </td>
                    <td className="px-2 py-2 text-sm">
                      {inv.invoice_number || "-"}
                    </td>
                    <td className="w-36 max-w-[6rem] truncate px-2 py-3 text-sm">
                      {inv.ticket_number || "-"}
                    </td>
                    <td className="px-2 py-2 text-sm">
                      {inv.gds_locator || "-"}
                    </td>
                    <td className="px-2 py-2 text-sm">
                      {inv?.customer_details?.full_name || "-"}
                    </td>
                    <td className="px-2 py-2 text-sm">
                      {(() => {
                        const value =
                          inv?.airticket?.airticket_to_supplier?.paymentMade -
                          inv?.airticket?.airticket_to_supplier?.total -
                          inv?.airticket?.airticket_to_supplier
                            ?.payment_received_from_supplier;

                        return value < 0 ? Math.abs(value).toFixed(2) : "0.00";
                      })()}
                    </td>

                    <td className="px-2 py-2 text-sm">
                      {(() => {
                        const value =
                          inv?.airticket?.airticket_to_supplier?.total -
                          inv?.airticket?.airticket_to_supplier?.paymentMade;

                        return value < 0 ? Math.abs(value).toFixed(2) : "0.00";
                      })()}

                      {}
                    </td>

                    <td className="px-2 py-2 text-sm">
                      {(() => {
                        const supplier =
                          inv?.airticket?.airticket_to_supplier || {};

                        const drRaw =
                          supplier?.paymentMade -
                          supplier?.total -
                          supplier?.payment_received_from_supplier;
                        const crRaw = supplier?.total - supplier?.paymentMade;

                        const dr = drRaw < 0 ? Math.abs(drRaw) : 0;
                        const cr = crRaw < 0 ? Math.abs(crRaw) : 0;

                        const totalBalance = dr - cr;

                        const displayValue = isNaN(drRaw)
                          ? "0.00"
                          : drRaw.toFixed(2);
                        return displayValue;
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="no-print">
                <tr>
                  <td
                    colSpan={5}
                    className="px-2 py-2 text-left text-sm font-medium"
                  >
                    Totals:
                  </td>
                  <td className="px-2 py-2 text-sm font-medium">
                    {totalDr.toFixed(2)}
                  </td>
                  <td className="px-2 py-2 text-sm font-medium">
                    {totalCr.toFixed(2)}
                  </td>
                  <td className="px-2 py-2 text-sm font-medium">
                    {totalBalance.toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </>
          )}
        </table>
      </div>

      {/* Pagination - hidden in print */}
      {isLoading ? null : (
        <div className="no-print flex items-center justify-between py-4">
          <div className="flex items-center space-x-2">
            <label htmlFor="pageSize" className="text-gray-800">
              Per Page:
            </label>
            <select
              id="pageSize"
              value={pagination.pageSize}
              onChange={(e) =>
                setPagination((prev) => ({
                  ...prev,
                  pageSize: Number(e.target.value),
                  pageIndex: 0,
                }))
              }
              className="rounded border border-gray-300 bg-white p-1 text-gray-800"
            >
              {[10, 50, 100, 500, 1000].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center space-x-4">
            <button
              onClick={() => handlePageChange(pagination.pageIndex - 1)}
              disabled={disablePrev}
              className={`rounded px-4 py-2 text-white ${
                disablePrev
                  ? "cursor-not-allowed bg-gray-400"
                  : "bg-blue-500 hover:bg-blue-600"
              }`}
            >
              Previous
            </button>

            <span className="text-gray-800">
              Page {pagination.pageIndex + 1} of {pagination.lastPage}
            </span>

            <button
              onClick={() => handlePageChange(pagination.pageIndex + 1)}
              disabled={disableNext}
              className={`rounded px-4 py-2 text-white ${
                disableNext
                  ? "cursor-not-allowed bg-gray-400"
                  : "bg-blue-500 hover:bg-blue-600"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
